<script setup lang="ts">
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    imageSrc: string
    fileName?: string
    outputSize?: number
  }>(),
  {
    fileName: 'avatar.jpg',
    outputSize: 512,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: [file: File]
  cancel: []
}>()

const { smAndDown } = useDisplay()
const imageRef = ref<HTMLImageElement | null>(null)
const cropper = ref<Cropper | null>(null)
const isProcessing = ref(false)

const imageCrossOrigin = computed(() =>
  props.imageSrc.startsWith('blob:') ? undefined : 'anonymous',
)

function destroyCropper() {
  cropper.value?.destroy()
  cropper.value = null
}

function initCropper() {
  const image = imageRef.value
  if (!image || !props.modelValue || !props.imageSrc) return

  destroyCropper()
  cropper.value = new Cropper(image, {
    aspectRatio: 1,
    viewMode: 1,
    dragMode: 'move',
    autoCropArea: 0.9,
    responsive: true,
    background: false,
    rotatable: true,
    scalable: true,
    zoomable: true,
    guides: true,
  })
}

async function setupCropper() {
  await nextTick()
  const image = imageRef.value
  if (!image || !props.modelValue) return

  if (image.complete && image.naturalWidth > 0) {
    initCropper()
  } else {
    image.onload = () => initCropper()
  }
}

watch(
  () => props.modelValue,
  open => {
    if (!open) destroyCropper()
  },
)

onBeforeUnmount(() => {
  destroyCropper()
})

function zoomIn() {
  cropper.value?.zoom(0.1)
}

function zoomOut() {
  cropper.value?.zoom(-0.1)
}

function rotateLeft() {
  cropper.value?.rotate(-90)
}

function rotateRight() {
  cropper.value?.rotate(90)
}

function resetCrop() {
  cropper.value?.reset()
}

function close() {
  emit('update:modelValue', false)
}

function cancel() {
  emit('cancel')
  close()
}

async function confirm() {
  if (!cropper.value || isProcessing.value) return

  isProcessing.value = true
  try {
    const canvas = cropper.value.getCroppedCanvas({
      width: props.outputSize,
      height: props.outputSize,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    })

    if (!canvas) return

    canvas.toBlob(
      blob => {
        if (!blob) {
          isProcessing.value = false
          return
        }
        const baseName = props.fileName.replace(/\.[^.]+$/, '')
        const file = new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' })
        emit('confirm', file)
        close()
        isProcessing.value = false
      },
      'image/jpeg',
      0.92,
    )
  } catch (error) {
    console.error('Ошибка кадрирования аватара:', error)
    isProcessing.value = false
  }
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="560"
    persistent
    content-class="entity-form-menu"
    @update:model-value="emit('update:modelValue', $event)"
    @after-enter="setupCropper"
  >
    <VCard>
      <!-- Шапка диалога по паттерну НС -->
      <VCardTitle class="d-flex pa-6 pb-2" :class="smAndDown ? 'px-6 py-4' : 'pa-6 pb-2'">
        <div class="form_card d-flex justify-space-between w-100">
          <div class="form_card__title">
            <span class="text-h4">
              Кадрирование аватара
            </span>
          </div>
        </div>
        <CustomCloseButton :close-action="cancel" />
      </VCardTitle>

      <VCardText class="pa-6 pt-0">
        <!-- Поясняющий текст НАД аватаркой -->
        <p class="text-body-1 text-medium-emphasis mb-4">
          Настройте положение и масштаб фото для аватара
        </p>

        <!-- Контейнер кроппера -->
        <div
          v-if="modelValue && imageSrc"
          class="avatar-crop__container mb-4"
        >
          <img
            ref="imageRef"
            :key="imageSrc"
            :src="imageSrc"
            alt="Avatar preview"
            :crossorigin="imageCrossOrigin"
            class="avatar-crop__image"
          >
        </div>

        <!-- Панель управления с тултипами по паттерну НС/Заявок -->
        <div class="d-flex align-center justify-center gap-3 mb-6 flex-wrap">
          <VTooltip location="top" text="Приблизить">
            <template #activator="{ props: tooltipProps }">
              <VBtn
                v-bind="tooltipProps"
                icon="ri-zoom-in-line"
                variant="tonal"
                class="avatar-crop__tool-btn"
                @click="zoomIn"
              />
            </template>
          </VTooltip>

          <VTooltip location="top" text="Отдалить">
            <template #activator="{ props: tooltipProps }">
              <VBtn
                v-bind="tooltipProps"
                icon="ri-zoom-out-line"
                variant="tonal"
                class="avatar-crop__tool-btn"
                @click="zoomOut"
              />
            </template>
          </VTooltip>

          <VTooltip location="top" text="Повернуть влево">
            <template #activator="{ props: tooltipProps }">
              <VBtn
                v-bind="tooltipProps"
                icon="ri-anticlockwise-line"
                variant="tonal"
                class="avatar-crop__tool-btn"
                @click="rotateLeft"
              />
            </template>
          </VTooltip>

          <VTooltip location="top" text="Повернуть вправо">
            <template #activator="{ props: tooltipProps }">
              <VBtn
                v-bind="tooltipProps"
                icon="ri-clockwise-line"
                variant="tonal"
                class="avatar-crop__tool-btn"
                @click="rotateRight"
              />
            </template>
          </VTooltip>

          <VTooltip location="top" text="Сбросить">
            <template #activator="{ props: tooltipProps }">
              <VBtn
                v-bind="tooltipProps"
                icon="ri-refresh-line"
                variant="tonal"
                class="avatar-crop__tool-btn"
                @click="resetCrop"
              />
            </template>
          </VTooltip>
        </div>

        <div class="d-flex justify-end">
          <VBtn
            color="primary"
            :loading="isProcessing"
            @click="confirm"
          >
            Сохранить
          </VBtn>
        </div>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style lang="scss">
.avatar-crop__container {
  width: 100%;
  height: 380px;
  overflow: hidden;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.04);

  .cropper-view-box,
  .cropper-face {
    border-radius: 50%;
  }

  .cropper-view-box {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-color: rgba(var(--v-theme-primary), 0.85);
  }
}

.avatar-crop__image {
  display: block;
  max-width: 100%;
}

.avatar-crop__tool-btn {
  width: 42px !important;
  height: 42px !important;
  min-width: 42px !important;
  border-radius: 8px !important;

  .v-icon {
    font-size: 22px !important;
  }
}
</style>
